import { useCallback, useEffect, useMemo, useState } from "react";
import { useManagerScope } from "@/components/ManagerLayout";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import TimeInput24 from "@/components/TimeInput24";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, Flag, Pencil, ExternalLink, ListChecks, ChevronLeft, ChevronRight } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

function fmtDate(iso) { if (!iso) return "—"; return new Date(iso).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }); }
function fmtTime(iso) { if (!iso) return "—"; return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }); }
function fmtCurrency(n) { return (n || 0).toLocaleString("en-NZ", { style: "currency", currency: "NZD" }); }
function isoDate(d) { return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); }
function mondayOf(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function formatWeekLabel(monday) { const sun = addDays(monday, 6); const fmt = (d) => d.toLocaleDateString([], { day: "numeric", month: "short" }); return `${fmt(monday)} – ${fmt(sun)} ${sun.getFullYear()}`; }
function toLocalDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function toLocalTimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function FlagDialog({ shift, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!note.trim()) { toast.error("Note required"); return; }
    setBusy(true);
    try {
      await api.post(`/manager/timesheets/${shift.id}/flag`, { note });
      toast.success("Flagged for admin review");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`flag-${shift.id}`}>
          <Flag className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Flag shift for admin review</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Note <span className="text-red-500">*</span></Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} required />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 text-white hover:bg-amber-700">{busy ? "Flagging…" : "Flag"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ shift, onDone }) {
  const [open, setOpen] = useState(false);
  const [sd, setSd] = useState(toLocalDateInput(shift.clock_in_ts));
  const [cit, setCit] = useState(toLocalTimeInput(shift.clock_in_ts));
  const [cot, setCot] = useState(toLocalTimeInput(shift.clock_out_ts));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const overnight = sd && cit && cot && cot < cit;
  const combine = (dateStr, timeStr, addDays = 0) => {
    if (!dateStr || !timeStr) return null;
    const [Y, M, D] = dateStr.split("-").map(Number);
    const [h, m] = timeStr.split(":").map(Number);
    return new Date(Y, M - 1, D + addDays, h, m, 0, 0);
  };
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      const body = { reason };
      const ci = combine(sd, cit, 0);
      const co = cot ? combine(sd, cot, overnight ? 1 : 0) : null;
      if (ci) body.clock_in = ci.toISOString();
      if (co) body.clock_out = co.toISOString();
      await api.patch(`/manager/timesheets/${shift.id}`, body);
      toast.success("Shift updated");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) { setSd(toLocalDateInput(shift.clock_in_ts)); setCit(toLocalTimeInput(shift.clock_in_ts)); setCot(toLocalTimeInput(shift.clock_out_ts)); setReason(""); } }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`edit-${shift.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit clock times</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Shift date</Label>
            <Input type="date" value={sd} onChange={(e) => setSd(e.target.value)} data-testid={`mgr-edit-date-${shift.id}`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Clock in (24h)</Label>
              <TimeInput24 value={cit} onChange={setCit} data-testid={`mgr-edit-cit-${shift.id}`} />
            </div>
            <div>
              <Label>Clock out (24h)</Label>
              <TimeInput24 value={cot} onChange={setCot} data-testid={`mgr-edit-cot-${shift.id}`} />
            </div>
          </div>
          {overnight && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              Clock-out is before clock-in — treating this as an overnight shift; clock-out will be saved as the next day.
            </div>
          )}
          <div>
            <Label>Reason <span className="text-red-500">*</span></Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} required placeholder="Required — recorded in audit log" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1A2744] text-white">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ManagerTimesheets() {
  const [searchParams] = useSearchParams();
  const [shifts, setShifts] = useState([]);
  const [status, setStatus] = useState(searchParams.get("status") || "ALL");
  const [selected, setSelected] = useState(new Set());
  const [summary, setSummary] = useState(null);
  const { scopeToken } = useManagerScope();
  const [view, setView] = useState("weekly"); // "weekly" | "all"
  const [groupBy, setGroupBy] = useState("employee"); // "employee" | "date"
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));

  const load = useCallback(async () => {
    const params = { status_filter: status };
    if (view === "weekly") {
      params.date_from = isoDate(weekStart);
      params.date_to = isoDate(addDays(weekStart, 6));
    }
    const { data } = await api.get("/manager/timesheets", { params });
    setShifts(data.shifts);
    setSelected(new Set());
    const { data: s } = await api.get("/manager/timesheets/summary");
    setSummary(s);
  }, [status, view, weekStart]);

  useEffect(() => { load(); }, [load, scopeToken]);

  const pg = usePagination(shifts, 10);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const selectCompleteAll = () => {
    const ids = shifts.filter((s) => s.status === "COMPLETE").map((s) => s.id);
    setSelected(new Set(ids));
  };

  const bulkApprove = async () => {
    if (!selected.size) return;
    try {
      const { data } = await api.post("/manager/timesheets/bulk-approve", { shift_ids: Array.from(selected) });
      toast.success(`${data.approved.length} approved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approveOne = async (id) => {
    try {
      await api.post(`/manager/timesheets/${id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const variancePill = (s) => {
    if (s.status === "INCOMPLETE") return <Badge className="bg-red-100 text-red-800 border-red-200">Incomplete</Badge>;
    return null;
  };

  // Weekly groupings (mirrors AdminTimesheets)
  const groups = useMemo(() => {
    if (view !== "weekly") return [];
    const map = new Map();
    for (const s of shifts) {
      let key, label, sortKey;
      if (groupBy === "employee") {
        key = s.user_id || s.employee_name || "unknown";
        label = (s.employee_name || "—") + (s.employee_code ? ` · ${s.employee_code}` : "");
        sortKey = (s.employee_name || "").toLowerCase();
      } else {
        const localDate = isoDate(new Date(s.clock_in_ts));
        key = localDate;
        label = new Date(s.clock_in_ts).toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
        sortKey = localDate;
      }
      if (!map.has(key)) map.set(key, { key, label, sortKey, shifts: [], totalMin: 0, totalCost: 0 });
      const g = map.get(key);
      g.shifts.push(s);
      g.totalMin += s.total_minutes || 0;
      g.totalCost += s.labour_cost || 0;
    }
    for (const g of map.values()) g.shifts.sort((a, b) => (a.clock_in_ts || "").localeCompare(b.clock_in_ts || ""));
    return [...map.values()].sort((a, b) => (a.sortKey > b.sortKey ? 1 : -1));
  }, [shifts, groupBy, view]);
  const weekTotalMin = useMemo(() => shifts.reduce((a, s) => a + (s.total_minutes || 0), 0), [shifts]);

  return (
    <div className="space-y-6" data-testid="mgr-timesheets-page">
      <header>
        <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">Timesheets</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] mt-1">Review & approve</h1>
        <p className="text-sm text-gray-500 mt-1">Approve shifts before admin syncs them to Xero.</p>
      </header>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold">This week · total shifts</div>
            <div className="font-heading text-2xl font-bold text-[#1A2744] mt-1" data-testid="sum-shifts">{summary.total_shifts}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold">Labour cost</div>
            <div className="font-heading text-2xl font-bold text-[#1A2744] mt-1" data-testid="sum-cost">{fmtCurrency(summary.total_labour_cost)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold">Pending approval</div>
            <div className="font-heading text-2xl font-bold text-amber-700 mt-1" data-testid="sum-pending">{summary.pending_approval_count}</div>
          </CardContent></Card>
          <Card className={summary.ready_for_xero ? "bg-emerald-50 border-emerald-200" : ""}>
            <CardContent className="p-4">
              <div className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold">Ready for Xero</div>
              <div className={`font-heading text-2xl font-bold mt-1 ${summary.ready_for_xero ? "text-emerald-700" : "text-gray-600"}`} data-testid="sum-ready">
                {summary.ready_for_xero ? "✅ Yes" : `${summary.pending_approval_count} to go`}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button onClick={() => setView("weekly")} className={`px-3 py-1.5 text-sm ${view === "weekly" ? "bg-[#1A2744] text-white" : "bg-white text-[#1A2744]"}`} data-testid="mgr-view-weekly">Weekly</button>
              <button onClick={() => setView("all")} className={`px-3 py-1.5 text-sm ${view === "all" ? "bg-[#1A2744] text-white" : "bg-white text-[#1A2744]"}`} data-testid="mgr-view-all">All shifts</button>
            </div>
            {view === "weekly" && (
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden ml-2">
                <button onClick={() => setGroupBy("employee")} className={`px-3 py-1.5 text-xs ${groupBy === "employee" ? "bg-[#C9A84C]/20 text-[#1A2744] font-semibold" : "bg-white text-gray-600"}`} data-testid="mgr-group-employee">By employee</button>
                <button onClick={() => setGroupBy("date")} className={`px-3 py-1.5 text-xs ${groupBy === "date" ? "bg-[#C9A84C]/20 text-[#1A2744] font-semibold" : "bg-white text-gray-600"}`} data-testid="mgr-group-date">By date</button>
              </div>
            )}
          </div>
          {view === "weekly" && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="mgr-week-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <div className="text-sm font-semibold text-[#1A2744] min-w-[160px] text-center" data-testid="mgr-week-label">{formatWeekLabel(weekStart)}</div>
              <Button size="sm" variant="outline" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="mgr-week-next"><ChevronRight className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="mgr-week-this">This week</Button>
              <div className="hidden md:block text-xs text-gray-500 border-l border-gray-200 pl-3 ml-1">
                Week total: <span className="font-semibold text-[#1A2744]">{(weekTotalMin / 60).toFixed(2)} hrs</span>
                <span className="mx-1">·</span>{shifts.length} shifts
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["ALL", "COMPLETE", "APPROVED", "INCOMPLETE", "FLAGGED", "ACTIVE"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={selectCompleteAll} data-testid="select-all-complete">
          <ListChecks className="h-4 w-4 mr-1" /> Select all COMPLETE
        </Button>
        {selected.size > 0 && (
          <Button size="sm" onClick={bulkApprove} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="bulk-approve">
            <Check className="h-4 w-4 mr-1" /> Approve {selected.size} selected
          </Button>
        )}
      </div>

      {view === "weekly" ? (
        <WeeklyMgrGroups groups={groups} groupBy={groupBy} selected={selected} toggle={toggle} approveOne={approveOne} load={load} variancePill={variancePill} />
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <MgrShiftsTable shifts={pg.pageItems} selected={selected} toggle={toggle} approveOne={approveOne} load={load} variancePill={variancePill} />
          </CardContent>
        </Card>
      )}
      {view === "all" && (
        <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="mgr-shifts" />
      )}
    </div>
  );
}

function WeeklyMgrGroups({ groups, groupBy, selected, toggle, approveOne, load, variancePill }) {
  if (groups.length === 0) {
    return <Card><CardContent className="py-12 text-center text-sm text-gray-500">No shifts in the selected week.</CardContent></Card>;
  }
  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <Card key={g.key} data-testid={`mgr-group-${g.key}`}>
          <CardContent className="p-0">
            <div className="px-4 py-3 flex items-center justify-between bg-[#1A2744]/[0.03] border-b border-gray-100">
              <div>
                <div className="font-semibold text-[#1A2744] text-sm">{groupBy === "employee" ? "👤" : "📅"} {g.label}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{g.shifts.length} shift{g.shifts.length === 1 ? "" : "s"}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Total</div>
                <div className="font-mono font-bold text-[#1A2744]">{(g.totalMin / 60).toFixed(2)} hrs</div>
                {g.totalCost > 0 && <div className="text-[10px] text-gray-500">{(g.totalCost || 0).toLocaleString("en-NZ", { style: "currency", currency: "NZD" })}</div>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <MgrShiftsTable shifts={g.shifts} selected={selected} toggle={toggle} approveOne={approveOne} load={load} variancePill={variancePill} hideEmployee={groupBy === "employee"} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function MgrShiftsTable({ shifts, selected, toggle, approveOne, load, variancePill, hideEmployee }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8"></TableHead>
          {!hideEmployee && <TableHead>Employee</TableHead>}
          <TableHead>Date</TableHead>
          <TableHead>In → Out</TableHead>
          <TableHead className="text-right">Hrs</TableHead>
          <TableHead className="text-right">Cost</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Sync</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {shifts.length === 0 && (
          <TableRow><TableCell colSpan={hideEmployee ? 8 : 9} className="text-center text-gray-500 py-8">No shifts for this filter.</TableCell></TableRow>
        )}
        {shifts.map((s) => (
          <TableRow key={s.id} data-testid={`shift-row-${s.id}`}>
            <TableCell>
              {s.status === "COMPLETE" && (
                <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} data-testid={`select-${s.id}`} />
              )}
            </TableCell>
            {!hideEmployee && (
              <TableCell>
                <div className="font-medium text-[#1A2744]">{s.employee_name}</div>
                <div className="text-[11px] text-gray-500">{s.employee_code}</div>
              </TableCell>
            )}
            <TableCell className="text-sm">{fmtDate(s.clock_in_ts)}</TableCell>
            <TableCell className="text-xs font-mono">
              {fmtTime(s.clock_in_ts)} → {fmtTime(s.clock_out_ts)}
            </TableCell>
            <TableCell className="text-right font-semibold">
              {s.total_minutes ? (s.total_minutes / 60).toFixed(2) : "—"}
              {s.break_minutes > 0 && <span className="ml-1 text-[10px] text-amber-700">-{s.break_minutes}m</span>}
            </TableCell>
            <TableCell className="text-right text-sm">{fmtCurrency(s.labour_cost)}</TableCell>
            <TableCell>
              <Badge variant="outline" className={
                s.status === "APPROVED" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                s.status === "COMPLETE" ? "bg-blue-50 text-blue-800 border-blue-200" :
                s.status === "INCOMPLETE" ? "bg-red-50 text-red-800 border-red-200" :
                s.status === "FLAGGED" ? "bg-amber-50 text-amber-800 border-amber-200" :
                "bg-gray-50 text-gray-700 border-gray-200"
              }>{s.status}</Badge>
              {variancePill(s)}
            </TableCell>
            <TableCell>
              <div className="flex flex-col gap-0.5 items-start">
                <Badge variant="outline" className="text-[10px]">{s.xero_sync_status || "NONE"}</Badge>
                {s.xero_timesheet_url && (
                  <a href={s.xero_timesheet_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-[#C9A84C] hover:underline">
                    <ExternalLink className="h-3 w-3" /> Xero
                  </a>
                )}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex gap-1 flex-wrap">
                {["COMPLETE", "FLAGGED"].includes(s.status) && (
                  <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => approveOne(s.id)} data-testid={`approve-${s.id}`}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                )}
                <EditDialog shift={s} onDone={load} />
                {s.status !== "APPROVED" && <FlagDialog shift={s} onDone={load} />}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
